import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner, faUsers, faBuilding, faCalendarAlt, faLock } from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import CustomMultiSelect from "../../../../common/formfields/CustomMultiSelect";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import apiClient from "../../../../../services/apiClient";
import apiServices from '../../../../../services/apiService';

const BulkDutyAllotment = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const vendorId = user?.vendorId || localStorage.getItem('vendorId');
    const assignedSiteId = user?.siteId || 2;

    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);

    const [staffList, setStaffList] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [shifts, setShifts] = useState([]);
    const [wings, setWings] = useState([]);
    const [floors, setFloors] = useState([]);
    const [units, setUnits] = useState([]);
    const siteName = user?.siteName || localStorage.getItem('siteName') || "Assigned Site";

    const [formData, setFormData] = useState({
        staffIds: [],
        siteId: assignedSiteId,
        wingId: null,
        floorId: null,
        unitId: null,
        shiftId: null,
        validFrom: "",
        validTo: ""
    });

    const [errors, setErrors] = useState({});

    // Fetch master data
    useEffect(() => {
        const fetchInitialData = async () => {
            setFetchingData(true);
            try {
                // Fetch Roles
                const roleRes = await apiClient.get('/staff-management/staff/GetRoles', {
                    params: { siteId: assignedSiteId }
                });
                if (roleRes.data?.isSuccess && roleRes.data?.data) {
                    setRoles(roleRes.data.data);
                }

                // Fetch Shifts
                const shiftRes = await apiClient.get('/shift-master/GetShifts', {
                    params: { personTypeId: 3 }
                });
                if (shiftRes.data?.isSuccess && shiftRes.data?.data) {
                    setShifts(shiftRes.data.data.map(s => ({
                        label: s.description || s.shiftName,
                        value: s.id
                    })));
                }

                // Fetch Wings
                const wingRes = await apiServices({ method: 'GET', url: '/WingMaster/GetAllWing' });
                if (wingRes.success) {
                    setWings(wingRes.data?.WingMasterData || []);
                }

                // Fetch Floors
                const floorRes = await apiServices({ method: 'GET', url: '/FloorMaster/GetAllFloor' });
                if (floorRes.success) {
                    setFloors(floorRes.data?.FloorMasterData || []);
                }

            } catch (err) {
                console.error("Error fetching initial data", err);
                toast.error("Failed to load necessary form data.");
            } finally {
                setFetchingData(false);
            }
        };

        fetchInitialData();
    }, [assignedSiteId]);

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const params = { 
                    status: 'Active',
                    siteId: assignedSiteId,
                    pageNumber: 1,
                    pageSize: 100
                };
                if (vendorId) params.vendorId = vendorId;
                if (selectedRole) params.staffRoleId = Number(selectedRole);

                const staffRes = await apiClient.get('/staff-management/staff/GetStaff', { params });
                if (staffRes.data?.isSuccess && staffRes.data?.data) {
                    const activeStaff = staffRes.data.data.filter(s => s.status === 'Active' || s.employmentStatus === 'Active');
                    setStaffList(activeStaff.map(s => ({
                        label: `${s.firstName || ''} ${s.lastName || ''} - ${s.roleName || ''}`,
                        value: s.staffId || s.id
                    })));
                } else {
                    setStaffList([]);
                }
            } catch (err) {
                console.error("Error fetching staff:", err);
                setStaffList([]);
            }
        };
        fetchStaff();
    }, [vendorId, selectedRole]);

    // Fetch units when floor changes
    useEffect(() => {
        const fetchUnits = async () => {
            if (!formData.siteId) {
                setUnits([]);
                return;
            }
            try {
                let url = `/UnitMaster/GetAllUnitMaster?siteId=${formData.siteId}`;
                if (formData.wingId) url += `&wingId=${formData.wingId}`;
                if (formData.floorId) url += `&floorId=${formData.floorId}`;
                url += `&pageNumber=1&pageSize=1000`;

                const unitRes = await apiServices({
                    method: 'GET',
                    url: url
                });

                if (unitRes.isSuccess || unitRes.success || unitRes.IsSuccess) {
                    const allUnits = unitRes.data?.data || unitRes.data || [];
                    
                    setUnits(allUnits.map(u => ({
                        label: u.UnitName || u.unitName,
                        value: u.Id || u.UnitId || u.unitId,
                        isStrict: u.IsStaffApprovalRequired || u.isStaffApprovalRequired || false
                    })));
                }
            } catch (err) {
                console.error("Error fetching units", err);
            }
        };

        fetchUnits();
    }, [formData.siteId, formData.wingId, formData.floorId]);

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const next = { ...prev, [key]: option ? (Array.isArray(option) ? option : option.value) : null };
            if (key === 'wingId') {
                next.floorId = null;
                next.unitId = null;
            }
            if (key === 'floorId') {
                next.unitId = null;
            }
            return next;
        });
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
    };

    const validateForm = () => {
        let newErrors = {};
        if (!formData.staffIds || formData.staffIds.length === 0) newErrors.staffIds = "Please select at least one staff member.";
        if (!formData.siteId) newErrors.siteId = "Site is required.";
        if (!formData.unitId) newErrors.unitId = "Unit is required.";
        if (!formData.shiftId) newErrors.shiftId = "Shift is required.";
        if (!formData.validFrom) newErrors.validFrom = "Start Date is required.";
        if (!formData.validTo) newErrors.validTo = "End Date is required.";
        else if (new Date(formData.validFrom) > new Date(formData.validTo)) newErrors.validTo = "End date must be after start date.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error("Please fill all required fields correctly.");
            return;
        }

        setLoading(true);

        const payload = {
            staffId: formData.staffIds.map(s => Number(s.value || s)),
            siteId: Number(formData.siteId),
            wingId: formData.wingId ? Number(formData.wingId) : null,
            floorId: formData.floorId ? Number(formData.floorId) : null,
            unitId: Number(formData.unitId),
            shiftId: Number(formData.shiftId),
            startDate: new Date(formData.validFrom).toISOString(),
            endDate: new Date(formData.validTo).toISOString(),
            assignedBy: user?.name || "VendorAdmin"
        };

        try {
            const response = await apiClient.post('/staff-management/staff/AllotDuty', payload);
            
            // Dynamic message handling based on user requirements
            if (response.data?.isSuccess) {
                toast.success(response.data?.message || "Duty allotted successfully.");
                navigate("/zentres/workforce-management/vendor/staff"); // Redirect to staff list or somewhere appropriate
            } else {
                toast.warning(response.data?.message || "Processed with some issues. Check status.");
            }
        } catch (error) {
            console.error("Duty Allotment error:", error);
            // Handle HTTP 400 Bad Request which might have dynamic validation messages from Policy Engine
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Failed to allot duty. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const selectedUnitObj = units.find(u => u.value === formData.unitId);
    const isSelectedUnitStrict = selectedUnitObj?.isStrict;

    const rowWings = wings
        .filter(w => !formData.siteId || (w.SiteId || w.siteId) == formData.siteId)
        .map(w => ({ label: w.WingName || w.wingName, value: w.Id || w.id || w.WingId }));

    const rowFloors = floors
        .filter(f => !formData.wingId || (f.WingId || f.wingId) == formData.wingId)
        .map(f => ({ label: f.FloorName || f.floorName || f.FloorNumber || f.floorNumber, value: f.Id || f.id || f.FloorId }));

    if (fetchingData) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 min-h-0 pb-12">
            <Breadcrumbs items={["Workforce Management", "Staff", "Bulk Duty Allotment"]} />

            <div className="mb-6 mt-4">
                <PageHeadingSection
                    Navbar={true}
                    mainHeading="Bulk Duty Allotment"
                    subHeading="Assign multiple staff members to a specific unit for a given timeframe."
                />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col">
                <form onSubmit={handleSubmit} className="p-6 md:p-8 flex flex-col">
                    <div className="flex flex-col gap-8 max-w-3xl">
                        
                        {/* Staff Selection */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Filter by Role
                                </label>
                                <select
                                    className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-cardbg text-gray-700 dark:text-textprimary text-sm rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 w-full min-h-[42px]"
                                    value={selectedRole}
                                    onChange={(e) => {
                                        setSelectedRole(e.target.value);
                                        setFormData(prev => ({ ...prev, staffIds: [] })); // Clear selected staff if role changes
                                    }}
                                >
                                    <option value="">All Roles</option>
                                    {roles.map((r) => (
                                        <option key={r.staffRoleId} value={r.staffRoleId}>
                                            {r.roleName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Select Staff Members <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <CustomMultiSelect
                                        options={staffList}
                                        value={formData.staffIds}
                                        onChange={(val) => handleSelectChange('staffIds', val)}
                                        placeholder="Search and select staff..."
                                    />
                                </div>
                                {errors.staffIds && <p className="text-red-500 text-[10px] mt-1">{errors.staffIds}</p>}
                                <p className="text-[10px] text-gray-400 mt-1">Select one or more active staff members from your vendor profile.</p>
                            </div>
                        </div>

                        {/* Location Selection */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Site</label>
                                <div className="border border-gray-200 bg-gray-50 text-gray-500 rounded-lg px-3 py-2 text-sm min-h-[42px] flex items-center">
                                    {siteName}
                                </div>
                            </div>

                            <div>
                                <CustomSelect
                                    label="Wing"
                                    options={rowWings}
                                    value={rowWings.find(w => w.value === formData.wingId) || null}
                                    onChange={(opt) => handleSelectChange('wingId', opt)}
                                    disabled={!formData.siteId}
                                />
                            </div>

                            <div>
                                <CustomSelect
                                    label="Floor"
                                    options={rowFloors}
                                    value={rowFloors.find(f => f.value === formData.floorId) || null}
                                    onChange={(opt) => handleSelectChange('floorId', opt)}
                                    disabled={!formData.wingId}
                                />
                            </div>

                            <div>
                                <CustomSelect
                                    label={<>Unit <span className="text-red-500">*</span></>}
                                    options={units}
                                    value={units.find(u => u.value === formData.unitId) || null}
                                    onChange={(opt) => handleSelectChange('unitId', opt)}
                                    disabled={!formData.siteId}
                                    error={errors.unitId}
                                    // Custom option rendering if possible to show lock icon, 
                                    // since CustomSelect might not support custom renders easily, we show indicator below
                                />
                                {errors.unitId && <p className="text-red-500 text-[10px] mt-1">{errors.unitId}</p>}
                                
                                {isSelectedUnitStrict && (
                                    <div className="flex items-center gap-1.5 mt-2 text-[10px] font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded w-fit border border-orange-100">
                                        <FontAwesomeIcon icon={faLock} />
                                        Strict Staff Approval Required
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Timeframe and Shift */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Duty Start Date <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        type="datetime-local" 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${errors.validFrom ? 'border-red-500' : 'border-gray-200'}`}
                                        value={formData.validFrom}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, validFrom: e.target.value }));
                                            if (errors.validFrom) setErrors(prev => ({ ...prev, validFrom: null }));
                                        }}
                                    />
                                </div>
                                {errors.validFrom && <p className="text-red-500 text-[10px] mt-1">{errors.validFrom}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Duty End Date <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        type="datetime-local" 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${errors.validTo ? 'border-red-500' : 'border-gray-200'}`}
                                        value={formData.validTo}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, validTo: e.target.value }));
                                            if (errors.validTo) setErrors(prev => ({ ...prev, validTo: null }));
                                        }}
                                        min={formData.validFrom}
                                    />
                                </div>
                                {errors.validTo && <p className="text-red-500 text-[10px] mt-1">{errors.validTo}</p>}
                            </div>

                            <div>
                                <CustomSelect
                                    label={<>Shift <span className="text-red-500">*</span></>}
                                    options={shifts}
                                    value={shifts.find(s => s.value === formData.shiftId) || null}
                                    onChange={(opt) => handleSelectChange('shiftId', opt)}
                                    error={errors.shiftId}
                                />
                                {errors.shiftId && <p className="text-red-500 text-[10px] mt-1">{errors.shiftId}</p>}
                            </div>
                        </div>

                    </div>

                    <div className="flex justify-between mt-10 border-t pt-4 shrink-0 max-w-3xl">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="px-3 py-2 border border-gray-200 rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            <FontAwesomeIcon icon={faAngleLeft} />
                            Cancel
                        </button>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                                style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                                    e.currentTarget.style.color = "#FFFFFF";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                    e.currentTarget.style.color = "var(--color-primaryaccent)";
                                }}
                            >
                                {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : "Allot Duty"}
                                <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BulkDutyAllotment;
